import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Pill, Activity,
  ClipboardList, FileCheck2, Lock, Loader2, ListChecks,
  Command as CommandIcon, CheckCircle2, AlertCircle, Clock,
  PanelRight, PanelRightClose,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEncounter } from '@/contexts/EncounterContext';
import { COCKPIT_NAV_ORDER, type CockpitSectionId } from './cockpit-nav';
import AmbientRecordControl from './AmbientRecordControl';

export type { CockpitSectionId };

interface NavItem {
  id: CockpitSectionId;
  label: string;
  icon: React.ElementType;
  badge?: number;
}

export interface PatientDemographics {
  dob?: string | null;
  sex?: string | null;
  mrn?: string | null;
}

export type CockpitSaveState = 'idle' | 'saving' | 'saved' | 'error';

interface VisitCockpitShellProps {
  active: CockpitSectionId;
  onChange: (id: CockpitSectionId) => void;
  onSign: () => void;
  signing?: boolean;
  navBadges?: Partial<Record<CockpitSectionId, number>>;
  children: React.ReactNode;
  /** Demographics rendered in the header chip (age / sex / MRN). */
  demographics?: PatientDemographics;
  /** Open the Cmd+K palette. Surfaced as a header hint button. */
  onOpenCommandPalette?: () => void;
  /** Cross-encounter save indicator (e.g. "Saved 2m ago"). */
  saveState?: CockpitSaveState;
  /** Reasons the encounter is incomplete; shown in tooltip on Sign button. */
  signIncompleteReasons?: string[];
  /**
   * Persistent context rail rendered to the right of the main pane (Phase 2).
   * Hidden on narrow viewports; collapsible by the practitioner.
   */
  rightRail?: React.ReactNode;
}

const calcAge = (dob?: string | null): number | null => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 130 ? age : null;
};

const formatDuration = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

const NAV: NavItem[] = [
  { id: 'overview', label: 'Overview', icon: ClipboardList },
  { id: 'chart', label: 'Chart', icon: FileText },
  { id: 'plan', label: 'Plan', icon: Pill },
  { id: 'context', label: 'Patient context', icon: Activity },
  { id: 'intake', label: 'Intake', icon: ListChecks },
  { id: 'avs', label: 'After-Visit Summary', icon: FileCheck2 },
];

// Tab order shared with `cockpit-nav.ts`. The list there is the source of
// truth for keyboard shortcuts; this NAV array drives icons / labels and
// must be kept in lockstep.
if (process.env.NODE_ENV !== 'production') {
  const navIds = NAV.map(n => n.id).join(',');
  const orderIds = COCKPIT_NAV_ORDER.join(',');
  if (navIds !== orderIds) {
    console.warn('[VisitCockpitShell] NAV order drift vs. cockpit-nav.ts:', navIds, '!=', orderIds);
  }
}

const VisitCockpitShell: React.FC<VisitCockpitShellProps> = ({
  active, onChange, onSign, signing, navBadges, children,
  demographics, onOpenCommandPalette, saveState = 'idle',
  signIncompleteReasons = [], rightRail,
}) => {
  // Persist rail collapse state per-user across visits.
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('cockpit:rail:collapsed') === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('cockpit:rail:collapsed', railCollapsed ? '1' : '0');
  }, [railCollapsed]);
  const navigate = useNavigate();
  const { encounter, appointment, isSigned } = useEncounter();
  const patientName = appointment?.patient
    ? `${appointment.patient.first_name} ${appointment.patient.last_name}`
    : encounter?.patient
      ? `${encounter.patient.first_name} ${encounter.patient.last_name}`
      : 'Patient';

  const visitDate = encounter?.start_time
    ? new Date(encounter.start_time).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
    : '';

  // Visit timer: ticks every second while in progress, freezes when signed.
  const startMs = encounter?.start_time ? new Date(encounter.start_time).getTime() : null;
  const endMs = encounter?.signed_at ? new Date(encounter.signed_at).getTime() : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (isSigned || !startMs) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isSigned, startMs]);
  const elapsedMs = startMs ? (endMs || now) - startMs : null;

  const age = calcAge(demographics?.dob);
  const sex = (demographics?.sex || '').toString().toLowerCase();
  const sexLabel = sex === 'male' || sex === 'm' ? 'M'
    : sex === 'female' || sex === 'f' ? 'F'
    : sex ? sex[0].toUpperCase() : null;
  const demoChipParts = [
    age != null ? `${age}y` : null,
    sexLabel,
  ].filter(Boolean) as string[];

  const isIncomplete = !isSigned && signIncompleteReasons.length > 0;

  return (
    <TooltipProvider delayDuration={300}>
    <div className='flex h-screen flex-col bg-slate-50'>
      {/* Header */}
      <header className='flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3'>
        <div className='flex items-center gap-3 min-w-0'>
          <Button
            variant='ghost'
            size='icon'
            onClick={() => navigate(-1)}
            aria-label='Back'
          >
            <ArrowLeft className='h-4 w-4' />
          </Button>
          <div className='min-w-0'>
            <div className='flex items-center gap-2 flex-wrap'>
              <h1 className='text-base font-semibold text-slate-900'>{patientName}</h1>
              {demoChipParts.length > 0 && (
                <Badge variant='outline' className='text-xs font-normal'>
                  {demoChipParts.join(' · ')}
                </Badge>
              )}
              {demographics?.mrn && (
                <Badge variant='outline' className='text-xs font-normal text-slate-500'>
                  MRN {demographics.mrn}
                </Badge>
              )}
              <Badge variant='outline' className='text-xs'>
                {visitDate}
              </Badge>
              {elapsedMs != null && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant='outline' className='text-xs font-normal'>
                      <Clock className='mr-1 h-3 w-3' />
                      {formatDuration(elapsedMs)}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isSigned ? 'Total visit duration' : 'Visit time so far — used for E/M coding'}
                  </TooltipContent>
                </Tooltip>
              )}
              {isSigned ? (
                <Badge className='bg-emerald-100 text-emerald-800 hover:bg-emerald-100'>
                  <Lock className='mr-1 h-3 w-3' />
                  Signed
                </Badge>
              ) : (
                <Badge className='bg-amber-100 text-amber-800 hover:bg-amber-100'>
                  In progress
                </Badge>
              )}
              <SaveIndicator state={saveState} />
            </div>
            {encounter?.chief_complaint && (
              <p className='mt-0.5 text-xs text-slate-500 truncate'>
                Chief complaint: {encounter.chief_complaint}
              </p>
            )}
          </div>
        </div>
        <div className='flex items-center gap-2'>
          {!isSigned && <AmbientRecordControl />}
          {onOpenCommandPalette && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={onOpenCommandPalette}
                  className='gap-1 text-slate-600'
                  aria-label='Open command palette'
                >
                  <CommandIcon className='h-3.5 w-3.5' />
                  <span className='hidden sm:inline'>{isMac ? '⌘K' : 'Ctrl K'}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Search & jump · {isMac ? '⌘K' : 'Ctrl+K'}</TooltipContent>
            </Tooltip>
          )}
          {!isSigned && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={onSign}
                    disabled={signing}
                    variant={isIncomplete ? 'outline' : 'default'}
                    className={cn(
                      isIncomplete
                        ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
                        : 'bg-brand-teal hover:bg-brand-teal/90',
                    )}
                  >
                    {signing ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Signing…
                      </>
                    ) : isIncomplete ? (
                      <>
                        <AlertCircle className='mr-2 h-4 w-4' />
                        Review &amp; Sign
                      </>
                    ) : (
                      <>
                        <Lock className='mr-2 h-4 w-4' />
                        Sign &amp; Close Visit
                      </>
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              {isIncomplete && (
                <TooltipContent className='max-w-xs'>
                  <p className='font-medium'>Visit looks incomplete</p>
                  <ul className='mt-1 space-y-0.5 text-xs'>
                    {signIncompleteReasons.map((r, i) => (
                      <li key={i}>· {r}</li>
                    ))}
                  </ul>
                  <p className='mt-1 text-xs text-slate-500'>You can still sign — the wizard confirms.</p>
                </TooltipContent>
              )}
            </Tooltip>
          )}
        </div>
      </header>

      {/* Body: nav rail + main */}
      <div className='flex flex-1 overflow-hidden'>
        <nav
          className='w-56 shrink-0 overflow-y-auto border-r border-slate-200 bg-white p-3'
          aria-label='Visit sections'
        >
          <ul className='space-y-1'>
            {NAV.map(item => {
              const Icon = item.icon;
              const badge = navBadges?.[item.id];
              return (
                <li key={item.id}>
                  <button
                    type='button'
                    onClick={() => onChange(item.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      active === item.id
                        ? 'bg-brand-teal/10 text-brand-teal'
                        : 'text-slate-600 hover:bg-slate-50',
                    )}
                  >
                    <Icon className='h-4 w-4 shrink-0' />
                    <span className='flex-1 text-left'>{item.label}</span>
                    {badge !== undefined && badge > 0 && (
                      <Badge variant='outline' className='h-5 min-w-5 text-[10px]'>
                        {badge}
                      </Badge>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <main className='flex-1 overflow-y-auto p-6 min-w-0'>{children}</main>

        {rightRail && (
          <aside
            className={cn(
              'shrink-0 border-l border-slate-200 bg-white transition-[width] duration-200',
              railCollapsed ? 'w-10' : 'w-[320px]',
              'hidden lg:flex flex-col',
            )}
            aria-label='Encounter context'
          >
            <div className='flex items-center justify-between border-b border-slate-200 p-2'>
              {!railCollapsed && (
                <span className='text-xs font-semibold uppercase tracking-wide text-slate-500'>
                  Patient context
                </span>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='h-6 w-6'
                    onClick={() => setRailCollapsed(c => !c)}
                    aria-label={railCollapsed ? 'Expand context rail' : 'Collapse context rail'}
                  >
                    {railCollapsed
                      ? <PanelRight className='h-4 w-4' />
                      : <PanelRightClose className='h-4 w-4' />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='left'>
                  {railCollapsed ? 'Show patient context' : 'Hide patient context'}
                </TooltipContent>
              </Tooltip>
            </div>
            {!railCollapsed && (
              <div className='flex-1 overflow-y-auto p-3'>{rightRail}</div>
            )}
          </aside>
        )}
      </div>
    </div>
    </TooltipProvider>
  );
};

const SaveIndicator: React.FC<{ state: CockpitSaveState }> = ({ state }) => {
  if (state === 'saving') {
    return (
      <span className='inline-flex items-center gap-1 text-[11px] text-slate-500'>
        <Loader2 className='h-3 w-3 animate-spin' /> Saving…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className='inline-flex items-center gap-1 text-[11px] text-emerald-600'>
        <CheckCircle2 className='h-3 w-3' /> Saved
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className='inline-flex items-center gap-1 text-[11px] text-rose-600'>
        <AlertCircle className='h-3 w-3' /> Not saved
      </span>
    );
  }
  return null;
};

export default VisitCockpitShell;
